import { match } from "ts-pattern";
import { BorrowState, Reward } from "./types";
import { MerklRewardLegacy } from "@silo/types";
import { BigMath } from "@silo/utils";

interface GetDepositAprMetricsParams {
  depositApr: bigint;
  depositRewardsApr: bigint;
  rewardsSymbol: string;
  isProtected: boolean;
  merklRewards: MerklRewardLegacy[];
}

interface DepositAprMetrics {
  hasDepositRewardsApr: boolean;
  totalDepositApr: bigint;
  hasDepositApr: boolean;
  depositRewards: Reward[];
}

export const getDepositAprMetrics = ({
  depositApr,
  depositRewardsApr,
  rewardsSymbol,
  isProtected,
  merklRewards,
}: GetDepositAprMetricsParams): DepositAprMetrics => {
  return match(isProtected)
    .returnType<DepositAprMetrics>()
    .with(false, () => {
      const collateralMerklRewards = merklRewards.filter(
        (reward) => reward.type === "collateral"
      );

      const allRewards: Reward[] = [
        { symbol: rewardsSymbol, apr: depositRewardsApr, isSuperfest: false },
        ...collateralMerklRewards,
      ];

      const rewards = allRewards
        .filter((reward) => reward.apr > 0n)
        .sort(sortAprDescending);

      const totalDepositRewardsApr = BigMath.sum(
        rewards.map((reward) => reward.apr)
      );
      const totalDepositApr = depositApr + totalDepositRewardsApr;

      return {
        hasDepositRewardsApr: totalDepositRewardsApr > 0n,
        totalDepositApr,
        hasDepositApr: true,
        depositRewards: rewards,
      };
    })
    .with(true, () => {
      const protectedMerklRewards = merklRewards.filter(
        (reward) => reward.type === "protected"
      );

      const allRewards: Reward[] = [
        { symbol: rewardsSymbol, apr: depositRewardsApr, isSuperfest: false },
        ...protectedMerklRewards,
      ];

      const rewards = allRewards
        .filter((reward) => reward.apr > 0n)
        .sort(sortAprDescending);

      const totalDepositRewardsApr = BigMath.sum(
        rewards.map((reward) => reward.apr)
      );

      const hasDepositRewardsApr = totalDepositRewardsApr > 0n;

      return {
        hasDepositRewardsApr,
        totalDepositApr: totalDepositRewardsApr,
        hasDepositApr: hasDepositRewardsApr,
        depositRewards: rewards,
      };
    })
    .exhaustive();
};

export const getBorrowState = (
  hasBorrowRewards: boolean,
  totalBorrowApr: bigint
): BorrowState => {
  if (!hasBorrowRewards) return "none";

  return totalBorrowApr >= 0n ? "netCost" : "netYield";
};

export const getBorrowStateColor = (borrowState: BorrowState) =>
  match(borrowState)
    .with("netCost", () => "text-amber-300")
    .with("netYield", () => "text-green-400")
    .with("none", () => "")
    .exhaustive();

export interface GetBorrowAprMetricsParams {
  borrowApr: bigint;
  borrowRewardsApr: bigint;
  rewardsSymbol: string;
  merklRewards: MerklRewardLegacy[];
}

interface BorrowAprMetrics {
  hasBorrowRewardsApr: boolean;
  totalBorrowApr: bigint;
  borrowRewards: Reward[];
}

export const getBorrowAprMetrics = ({
  merklRewards,
  rewardsSymbol,
  borrowRewardsApr,
  borrowApr,
}: GetBorrowAprMetricsParams): BorrowAprMetrics => {
  const debtMerklRewards = merklRewards.filter(
    (reward) => reward.type === "debt"
  );

  const allRewards: Reward[] = [
    { symbol: rewardsSymbol, apr: borrowRewardsApr, isSuperfest: false },
    ...debtMerklRewards,
  ];

  const rewards = allRewards
    .filter((reward) => reward.apr > 0n)
    .sort(sortAprDescending);

  const totalBorrowRewardsApr = BigMath.sum(
    rewards.map((reward) => reward.apr)
  );

  const totalBorrowApr = borrowApr - totalBorrowRewardsApr;

  return {
    hasBorrowRewardsApr: totalBorrowRewardsApr > 0n,
    totalBorrowApr,
    borrowRewards: rewards,
  };
};

function sortAprDescending<T extends { apr: bigint }>(a: T, b: T): number {
  if (a.apr > b.apr) {
    return -1;
  } else if (a.apr < b.apr) {
    return 1;
  } else {
    return 0;
  }
}
